/* ===========================================================================
 *  Datums, namen en getallen
 *
 *  Overgenomen uit Projects (src/lib/opmaak.ts daar), zonder wat over taken
 *  gaat. Een datum hier staat er zo als in het dashboard.
 * =========================================================================== */

const dag = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' })
const dagJaar = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
const volledig = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const volledigJaar = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })

export function datum(w: number | null | undefined): string {
  if (!w) return ''
  const d = new Date(w)
  return d.getFullYear() === new Date().getFullYear() ? dag.format(d) : dagJaar.format(d)
}

export function datumJaar(w: number | null | undefined): string {
  return w ? dagJaar.format(new Date(w)) : ''
}

export function datumTijd(w: number | null | undefined): string {
  if (!w) return ''
  const d = new Date(w)
  return d.getFullYear() === new Date().getFullYear() ? volledig.format(d) : volledigJaar.format(d)
}

export function startVanDag(t: number): number {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** "zojuist", "12 min geleden", "gisteren", of een datum. */
export function geleden(w: number | null | undefined): string {
  if (!w) return ''
  const s = Math.round((Date.now() - w) / 1000)
  if (s < 45) return 'zojuist'
  if (s < 3600) return `${Math.round(s / 60)} min geleden`
  if (s < 86400) return `${Math.round(s / 3600)} uur geleden`
  const dagen = Math.floor((startVanDag(Date.now()) - startVanDag(w)) / 86400000)
  if (dagen === 1) return 'gisteren'
  if (dagen < 7) return `${dagen} dagen geleden`
  return datum(w)
}

/** "vandaag", "over 3 dagen", "2 dagen geleden verlopen". */
export function termijn(w: number | null | undefined): string {
  if (!w) return ''
  const dagen = Math.round((startVanDag(w) - startVanDag(Date.now())) / 86400000)
  if (dagen === 0) return 'vandaag'
  if (dagen === 1) return 'morgen'
  if (dagen === -1) return 'gisteren'
  if (dagen < 0) return `${-dagen} dagen geleden`
  if (dagen < 45) return `over ${dagen} dagen`
  return datum(w)
}

/** Voor <input type="date">: yyyy-mm-dd in de eigen tijdzone. */
export function naarInvoer(t: number | null | undefined): string {
  if (!t) return ''
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Terug: om 12:00, zodat een tijdzone hem niet een dag verschuift. */
export function vanInvoer(w: string): number | null {
  if (!w) return null
  const t = new Date(w + 'T12:00:00').getTime()
  return Number.isFinite(t) ? t : null
}

/** Een datum die als tekst in `velden` staat (yyyy-mm-dd). */
export function veldDatum(w: string | undefined): number | null {
  return w && /^\d{4}-\d{2}-\d{2}$/.test(w) ? vanInvoer(w) : null
}

export const geld = (n: number | null | undefined) => (n === null || n === undefined ? '' : euro.format(n))

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function initialen(naam: string | null | undefined): string {
  if (!naam) return '?'
  const delen = naam.trim().split(/\s+/).filter(Boolean)
  const eerste = delen[0]?.[0] ?? '?'
  const laatste = delen.length > 1 ? delen[delen.length - 1]?.[0] ?? '' : ''
  return (eerste + laatste).toUpperCase()
}

/** Een vaste kleur per persoon, uit de naam, zodat iemand overal dezelfde is. */
export function persoonKleur(sleutel: string | null | undefined): string {
  const lijst = ['info', 'ok', 'paars', 'oranje', 'brand', 'danger', 'warn']
  let h = 0
  for (const c of sleutel ?? '') h = (h * 31 + c.charCodeAt(0)) >>> 0
  return lijst[h % lijst.length]!
}

export const meervoud = (n: number, een: string, meer: string) => `${n} ${n === 1 ? een : meer}`

/** Het adres zonder https:// en zonder streep aan het eind, om te tonen. */
export const kaalAdres = (u: string) => u.replace(/^https?:\/\//, '').replace(/\/$/, '')

/** Zoeken zonder op hoofdletters of accenten te struikelen. */
export function past(tekst: (string | null | undefined)[], q: string): boolean {
  if (!q.trim()) return true
  const kaal = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const alles = kaal(tekst.filter(Boolean).join(' '))
  return kaal(q).split(/\s+/).filter(Boolean).every((w) => alles.includes(w))
}
